// src/api/campaignApi.ts
import axios from 'axios';
import config from '../config';
import { store } from '../store/store';
import { logout } from '../reducers/authSlice';
import { isSessionOver } from './axiosInstance';

/**
 * Dedicated axios instance for the owner-only e-mail campaign screen.
 *
 * Same reasoning as `invoicingApi`: the shared `api` instance dispatches `logout()` on any 403 (see
 * api/axiosInstance.ts), and this screen answers 403 for reasons that have nothing to do with an
 * expired session. Being signed out of the whole site part-way through a send would be a genuinely
 * bad outcome — the send is resumable, but only if you can still reach the page.
 *
 * A dead session is the one thing it does have to act on, and it cannot be recognised from the
 * status: the API answers a missing, expired or invalid token with 403 as well right now, so the
 * screen would sit there re-sending a token that will never work again, showing a machine code and
 * offering no way back to the login form. `isSessionOver` splits the two by reading the error code
 * out of the body, which only the auth entry point writes — so "not an admin" still reaches the
 * screen's own message, and a dead token still clears the store.
 */
const campaignApi = axios.create({
    baseURL: config.API_URL,
});

campaignApi.interceptors.request.use(
    (requestConfig) => {
        const token = store.getState().auth.accessToken;
        if (token) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        return requestConfig;
    },
    (error) => Promise.reject(error)
);

campaignApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Session over, never a plain authorisation 403. That distinction is the whole reason this
        // instance exists, and isSessionOver draws it from the body rather than the status so it
        // keeps holding when the server goes back to answering 401.
        if (await isSessionOver(error)) {
            store.dispatch(logout());
        }
        return Promise.reject(error);
    }
);

export default campaignApi;
